import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { 
  Activity, UserPlus, CreditCard, Map, 
  MessageSquare, CheckCircle, Users, Calendar,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const actionIcons = {
  member_joined: { icon: UserPlus, color: 'text-blue-400', bg: 'bg-blue-400/20' },
  member_left: { icon: Users, color: 'text-red-400', bg: 'bg-red-400/20' },
  payment_created: { icon: CreditCard, color: 'text-green-400', bg: 'bg-green-400/20' },
  payment_made: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-400/20' },
  itinerary_added: { icon: Calendar, color: 'text-purple-400', bg: 'bg-purple-400/20' },
  itinerary_updated: { icon: Calendar, color: 'text-purple-400', bg: 'bg-purple-400/20' },
  trip_created: { icon: Map, color: 'text-orange-400', bg: 'bg-orange-400/20' },
  comment_added: { icon: MessageSquare, color: 'text-cyan-400', bg: 'bg-cyan-400/20' },
  vote_cast: { icon: CheckCircle, color: 'text-yellow-400', bg: 'bg-yellow-400/20' },
  review_added: { icon: MessageSquare, color: 'text-pink-400', bg: 'bg-pink-400/20' },
  default: { icon: Activity, color: 'text-slate-400', bg: 'bg-slate-400/20' }
};

const actionLabels = {
  member_joined: 'entrou na viagem',
  member_left: 'saiu da viagem',
  payment_created: 'criou um pagamento',
  payment_made: 'fez um pagamento',
  itinerary_added: 'adicionou ao roteiro',
  itinerary_updated: 'atualizou o roteiro',
  trip_created: 'criou uma viagem',
  comment_added: 'comentou',
  vote_cast: 'votou',
  review_added: 'adicionou uma avaliação',
};

export default function AdminActivity() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 30;

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/activity-log', {
        params: { skip: page * limit, limit }
      });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Erro ao carregar atividades');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const getActionConfig = (actionType) => {
    return actionIcons[actionType] || actionIcons.default;
  };

  const getActionLabel = (actionType) => {
    return actionLabels[actionType] || actionType;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Atividades</h1>
          <p className="text-slate-400">Log de atividades da plataforma</p>
        </div>
        <Badge className="bg-purple-500/20 text-purple-400">
          {total} atividades
        </Badge>
      </div>

      {/* Activity Timeline */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-400" />
            Timeline de Atividades
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length > 0 ? (
            <div className="space-y-4">
              {logs.map((log, index) => {
                const config = getActionConfig(log.action_type);
                const Icon = config.icon;
                const timestamp = log.timestamp ? parseISO(log.timestamp) : new Date();
                
                return (
                  <div 
                    key={log.id || index}
                    className="flex items-start gap-4 p-4 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors"
                  >
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${config.color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">
                          {log.user_name || 'Usuário'}
                        </span>
                        <span className="text-slate-400">
                          {getActionLabel(log.action_type)}
                        </span>
                        {log.trip_name && (
                          <Badge className="bg-slate-600 text-slate-300 text-xs">
                            {log.trip_name}
                          </Badge>
                        )}
                      </div>
                      
                      {log.details && (
                        <p className="text-sm text-slate-400 mt-1 truncate">
                          {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                        </p>
                      )}
                    </div>

                    {/* Timestamp */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm text-slate-400">
                        {formatDistanceToNow(timestamp, { addSuffix: true, locale: ptBR })}
                      </p>
                      <p className="text-xs text-slate-500">
                        {format(timestamp, "dd/MM HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-slate-400">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma atividade registrada ainda.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, total)} de {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-slate-700 text-slate-300"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="border-slate-700 text-slate-300"
            >
              Próximo
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
